import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  IconButton,
  Tooltip,
  Divider,
  Box,
  CssBaseline,
  useMediaQuery,
  List,
  Typography,
  Button,
} from "@mui/material";
import PageLoader from "../theme/GenericLoader/PageLoader";
import {
  startApiLoading,
  endApiLoading,
} from "../utilities/apiLoadingManager";
import {
  RouteTransitionStart,
  RouteTransitionReady,
} from "../components/routing/RouteTransitionLoader";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
  Logout,
} from "@mui/icons-material";
import { getMenuConfig, getAppMenuCatalog, HOMS_SETTINGS_MENU_ITEMS, HPRMS_SETTINGS_MENU_ITEMS, HPOS_SETTINGS_MENU_ITEMS } from "./MenuConfig";
import {
  DRAWER_WIDTH,
  COLLAPSED_DRAWER_WIDTH,
  MOBILE_BREAKPOINT,
  LayoutContainer,
  SidebarContainer,
  DesktopDrawer,
  MobileDrawer,
  DrawerHeader,
  LogoSection,
  LogoText,
  LogoIcon,
  ToggleButton,
  UserSection,
  UserAvatar,
  UserInfo,
  SectionTitle,
  DrawerFooter,
  MenuContent,
  StyledListItem,
  StyledListItemIcon,
  StyledListItemText,
  StyledBadge,
  MobileCloseButton,
  MainContent,
  CustomBackdrop
} from "./DrawerStyles";
import { MobileDrawerProvider } from "../theme/GenericHeader/MobileDrawerContext";
import { useTheme } from "@mui/material/styles";
import {
  Link,
  useLocation,
  useHistory,
} from "react-router-dom";
import { useSelector } from "react-redux";

// Import assets and utilities
import Store from "../Redux/store";
import helpers from "../UtilitiesFunctions";
import ThemeToggle from "../theme/ThemeToggle";
import HomSetupGuard from "../components/settings/HomSetupGuard";
import AccessDenied from "../Pages/AccessDenied/AccessDenied";
import { hasZatcaApp } from "../utilities/zatcaReceiptUtils";
import { ZATCA_MENU_PATHS } from "../utilities/taxConfigurationUtils";
import {
  filterHprmsMenuSections,
  isHprmsAppType,
  isHprmsPathEnabled,
} from "../utilities/moduleConfigurationUtils";
import {
  filterHomsMenuSections,
  isHomsAppType,
  isHomsPathEnabled,
} from "../utilities/homsModuleConfigurationUtils";
import { normalizeUserRole } from "../utilities/userRoleUtils";
import { hasAppShellAccess, menuKeyFromUiBucket, primaryUiBucket } from "../utilities/roleCapabilities";
import {
  filterMenuSectionsByAppPermissions,
  appendAppSettingsMenuSection,
  normalizeAuthzAppType,
  isAuthzAppAccessDenied,
  isAuthzAppAdmin,
  isAuthzBootstrapPending,
  resolveProfileLabelForApp,
  shouldUsePermissionUi,
} from "../utilities/authzPermissions";
import { withLaunchSearch } from "../utilities/appSessionKey";

function ResponsiveDrawer({
  token,
  userInfo = { preferred_username: "guest" },
  userRole,
  authenticated = false,
  cartlength,
  bussinessType,
  appType,
  onLogout,
  keycloak,
  children,
}) {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));

  // STATE MANAGEMENT
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  // Redux state
  const { companyName, instanceId, keyCloakAuth, gatewayUrl, basePath } = useSelector(
    (state) => state.globalState,
  );
  const purchasedApps = useSelector(
    (state) => state.purchasedAppState?.purchasedApps || [],
  );
  const moduleFlags = useSelector(
    (state) => state.moduleConfigState?.modules,
  );
  const pageFlags = useSelector(
    (state) => state.moduleConfigState?.pages,
  );
  const authzStatus = useSelector((state) => state.authzState?.status);
  const authzProfile = useSelector((state) => state.authzState?.profile);
  const authzProfiles = useSelector(
    (state) => state.authzState?.profiles || [],
  );
  const authzPermissions = useSelector(
    (state) => state.authzState?.permissions || [],
  );
  const zatcaAppPurchased = hasZatcaApp(purchasedApps);

  // EFFECT TO HANDLE SCREEN SIZE CHANGES
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  // HANDLERS
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const logout = async () => {
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      helpers.clearData();
      Store.dispatch({ type: "USER_LOGOUT" });
    }
    history.push(withLaunchSearch("/"));
  };

  const handleMenuAction = (action) => {
    if (action === "logout") {
      logout();
    }
    if (isMobile) {
      handleMobileDrawerClose();
    }
  };

  const normalizedRole = normalizeUserRole(userRole);
  const authzState = {
    status: authzStatus,
    permissions: authzPermissions,
    profile: authzProfile,
    profiles: authzProfiles,
  };
  const authzPending = isAuthzBootstrapPending(appType, authzState);
  const authzDenied = isAuthzAppAccessDenied(appType, authzState);
  const usePermissionUi = shouldUsePermissionUi(appType, authzState);
  const appProfileLabel = resolveProfileLabelForApp(authzState, appType);
  const displayRoleLabel = authzPending
    ? "Loading access..."
    : authzDenied
      ? "No app access"
      : usePermissionUi && appProfileLabel
        ? appProfileLabel
        : userRole || "Admin";
  // AuthZ apps: Keycloak Viewer alone is not enough once /auth/me has resolved.
  const canShowAppShell = authzPending
    ? true
    : authzDenied
      ? false
      : usePermissionUi || hasAppShellAccess(normalizedRole);

  const getCurrentMenuItems = () => {
    const bussinessType = Store.getState()?.globalState?.bussinessType;

    if (authzPending || authzDenied) {
      return [
        {
          section: "ACCOUNT",
          items: [{ name: "Logout", icon: <Logout />, action: "logout" }],
        },
      ];
    }

    const uiMenuKey = menuKeyFromUiBucket(primaryUiBucket(normalizedRole));
    const authzApp = normalizeAuthzAppType(appType);
    const authzAdmin = usePermissionUi && isAuthzAppAdmin(appType, authzState);

    let sections;
    if (usePermissionUi && authzApp) {
      // Permission → UI: full catalog filtered by /auth/me (not role bucket templates).
      sections = getAppMenuCatalog(appType, bussinessType, cartlength);
    } else {
      sections = getMenuConfig(
        normalizedRole,
        appType,
        bussinessType,
        cartlength,
        uiMenuKey
      );
    }

    if (!zatcaAppPurchased) {
      sections = sections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => !item.path || !ZATCA_MENU_PATHS.includes(item.path)
          ),
        }))
        .filter((section) => section.items.length > 0);
    }

    // UMS app admins: permission filter only (module flags must not hide catalog/settings).
    if (isHprmsAppType(appType) && !authzAdmin) {
      sections = filterHprmsMenuSections(sections, moduleFlags, pageFlags);
    }

    if (isHomsAppType(appType) && !authzAdmin) {
      sections = filterHomsMenuSections(sections, moduleFlags, pageFlags);
    }
    if (usePermissionUi && authzApp) {
      sections = filterMenuSectionsByAppPermissions(sections, authzPermissions, authzApp);
      const settingsPathEnabled = authzAdmin
        ? () => true
        : (path) => isHprmsPathEnabled(path, moduleFlags, pageFlags);
      const homsSettingsPathEnabled = authzAdmin
        ? () => true
        : (path) => isHomsPathEnabled(path, moduleFlags, pageFlags);

      if (authzApp === "HOMS") {
        sections = appendAppSettingsMenuSection(
          sections,
          authzPermissions,
          HOMS_SETTINGS_MENU_ITEMS,
          authzApp,
          homsSettingsPathEnabled
        );
      } else if (authzApp === "HPRMS") {
        sections = appendAppSettingsMenuSection(
          sections,
          authzPermissions,
          HPRMS_SETTINGS_MENU_ITEMS,
          authzApp,
          settingsPathEnabled
        );
      } else if (authzApp === "HPOS") {
        sections = appendAppSettingsMenuSection(
          sections,
          authzPermissions,
          HPOS_SETTINGS_MENU_ITEMS,
          authzApp
        );
      }
    }

    return sections;
  };

  const menuPaths = getCurrentMenuItems()
    .flatMap((section) => section.items || [])
    .map((item) => item.path)
    .filter(Boolean);

  /**
   * Mark only the best (longest) matching menu path as active.
   * Prevents /inventory lighting up on /inventory/reorder-alerts.
   */
  const isActive = (path) => {
    if (!path) return false;
    const pathname = location.pathname || "";
    if (path === "/") return pathname === "/";

    const matches =
      pathname === path || pathname.startsWith(`${path}/`);
    if (!matches) return false;

    const hasMoreSpecificMatch = menuPaths.some(
      (other) =>
        other !== path &&
        other.length > path.length &&
        (pathname === other || pathname.startsWith(`${other}/`))
    );
    return !hasMoreSpecificMatch;
  };

  const renderMenuItem = (item, index, drawerOpen) => {
    const isItemActive = isActive(item.path);
    // Handle action items (like logout)
    if (item.action) {
      const actionItem = (
        <StyledListItem
          onClick={() => handleMenuAction(item.action)}
          open={drawerOpen}
          key={index}
        >
          <StyledListItemIcon open={drawerOpen}>
            {item.badge ? (
              <StyledBadge badgeContent={item.badge}>{item.icon}</StyledBadge>
            ) : (
              item.icon
            )}
          </StyledListItemIcon>
          <StyledListItemText primary={item.name} open={drawerOpen} />
        </StyledListItem>
      );
      if (!drawerOpen) {
        return (
          <Tooltip
            key={index}
            title={item.name}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: theme.palette.text.primary,
                  color: theme.palette.common.white,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                },
              },
              arrow: {
                sx: {
                  color: theme.palette.text.primary,
                },
              },
            }}
          >
            {actionItem}
          </Tooltip>
        );
      }
      return actionItem;
    }
    // Handle navigation items (with path)
    const listItem = (
      <StyledListItem
        component={Link}
        to={withLaunchSearch(item.path)}
        active={isItemActive}
        open={drawerOpen}
        key={index}
        onClick={() => {
          if (isMobile) {
            handleMobileDrawerClose();
          }
        }}
      >
        <StyledListItemIcon open={drawerOpen}>
          {item.badge ? (
            <StyledBadge badgeContent={item.badge}>{item.icon}</StyledBadge>
          ) : (
            item.icon
          )}
        </StyledListItemIcon>
        <StyledListItemText primary={item.name} open={drawerOpen} />
      </StyledListItem>
    );
    if (!drawerOpen) {
      return (
        <Tooltip
          key={index}
          title={item.name}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.text.primary,
                color: theme.palette.common.white,
                fontSize: "0.75rem",
                fontWeight: 500,
              },
            },
            arrow: {
              sx: {
                color: theme.palette.text.primary,
              },
            },
          }}
        >
          {listItem}
        </Tooltip>
      );
    }
    return listItem;
  };

  // DRAWER CONTENT
  const drawer = (drawerOpen, isMobileDrawer = false) => (
    <>
      {/* Header */}
      <DrawerHeader open={drawerOpen}>
        <LogoSection open={drawerOpen}>
          {(() => {
            const cleanType = appType ? appType.replace('-', '') : '';

            // Only keep icons for non-WEBSITE apps
            const iconMap = {
              'HPOS': `${process.env.PUBLIC_URL}/google-analytics.png`,
              'HPiMS': `${process.env.PUBLIC_URL}/inventory.png`,
              'HCRM': `${process.env.PUBLIC_URL}/crm.png`,
              'HOMS': `${process.env.PUBLIC_URL}/checklist.png`,
              'HPRMS': `${process.env.PUBLIC_URL}/procurement.png`,
              'HRMS': `${process.env.PUBLIC_URL}/report.png`,
              'HVMS': `${process.env.PUBLIC_URL}/vendor.png`,
              'ZATCA': `${process.env.PUBLIC_URL}/ZATCA.png`,
              'ACCOUNTS': `${process.env.PUBLIC_URL}/ACCOUNTS.png`,
            };
            const iconSrc = iconMap[cleanType];

            return (
              <>
                {iconSrc ? (
                  <img
                    src={iconSrc}
                    alt={cleanType}
                    style={{
                      height: drawerOpen ? '30px' : '30px',
                      width: 'auto',
                      transition: 'height 0.2s ease'
                    }}
                  />
                ) : (
                  <LogoIcon />
                )}
                <LogoText variant="h6" open={drawerOpen}>
                  {cleanType || 'HULM SUITS'}
                </LogoText>
              </>
            );
          })()}
        </LogoSection>
        {isMobileDrawer && (
          <MobileCloseButton onClick={handleMobileDrawerClose}>
            <CloseIcon />
          </MobileCloseButton>
        )}
      </DrawerHeader>

      {/* Scrollable Menu Content */}
      <MenuContent>
        <Box sx={{ py: 4 }}>
          {/* Menu Sections - Filter out ACCOUNT section */}
          {getCurrentMenuItems().filter(section => section.section !== "ACCOUNT").map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              <SectionTitle open={drawerOpen}>{section.section}</SectionTitle>
              <List disablePadding sx={{ mb: 1 }}>
                {section.items.map((item, itemIndex) =>
                  renderMenuItem(
                    item,
                    `${sectionIndex}-${itemIndex}`,
                    drawerOpen,
                  ),
                )}
              </List>

              {sectionIndex < getCurrentMenuItems().filter(s => s.section !== "ACCOUNT").length - 1 &&
                drawerOpen && (
                  <Box sx={{ mx: 2, my: 0.5 }}>
                    <Divider sx={{ backgroundColor: theme.palette.divider }} />
                  </Box>
                )}
            </Box>
          ))}
        </Box>
      </MenuContent>

      <DrawerFooter>
        {/* Go to Apps Button */}
        <Box sx={{ p: drawerOpen ? 2 : 1, pb: 0, display: 'flex', justifyContent: 'center' }}>
          {drawerOpen ? (
            <Button
              fullWidth
              variant="outlined"
              href="https://app.hulmsolutions.com/dashboard"
              target="_blank"
              startIcon={<AppsIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              Go to Apps
            </Button>
          ) : (
            <Tooltip title="Go to Apps" placement="right">
              <IconButton
                href="https://app.hulmsolutions.com/dashboard"
                target="_blank"
                sx={{
                  color: theme.palette.primary.dark,
                  border: `1px solid ${theme.palette.primary.dark}`,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <AppsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {userInfo && (
          <UserSection open={drawerOpen}>
            <UserAvatar>
              {userInfo.name ? (
                userInfo.name.charAt(0).toUpperCase()
              ) : (
                <PersonIcon />
              )}
            </UserAvatar>
            <UserInfo open={drawerOpen}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                {userInfo.name || userInfo.given_name || "Super Admin"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {displayRoleLabel}
              </Typography>
            </UserInfo>
            {/* Theme Toggle next to user */}
            <Box sx={{ ml: 'auto' }}>
              <ThemeToggle sx={{ width: 32, height: 32 }} />
            </Box>
          </UserSection>
        )}

        {/* Account Section (Logout) */}
        <List disablePadding sx={{ mb: 0 }}>
          {getCurrentMenuItems()
            .find(section => section.section === "ACCOUNT")
            ?.items.map((item, index) =>
              renderMenuItem(item, `footer-${index}`, drawerOpen)
            )}
        </List>
      </DrawerFooter>
    </>
  );

  // PREVENT BODY SCROLL WHEN MOBILE DRAWER IS OPEN
  useEffect(() => {
    if (mobileOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileOpen, isMobile]);

  return (
    <MobileDrawerProvider isMobile={isMobile} openMobileDrawer={handleMobileDrawerToggle}>
      <LayoutContainer>
        <CssBaseline />

        {/* Sidebar - Only render when authenticated and authorized for non-WEBSITE apps */}
        {authenticated && canShowAppShell && appType !== "WEBSITE-" && (
          <>
            {/* Desktop Sidebar */}
            <SidebarContainer>
              <DesktopDrawer open={desktopOpen}>
                {drawer(desktopOpen, false)}
                <ToggleButton onClick={handleDesktopDrawerToggle} size="small" open={desktopOpen}>
                  {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </ToggleButton>
              </DesktopDrawer>
            </SidebarContainer>

            {/* Mobile Drawer */}
            {isMobile && (
              <>
                <CustomBackdrop
                  open={mobileOpen}
                  onClick={handleMobileDrawerClose}
                />
                <MobileDrawer
                  variant="temporary"
                  open={mobileOpen}
                  onClose={handleMobileDrawerClose}
                  ModalProps={{
                    keepMounted: true,
                    disableScrollLock: false,
                  }}
                >
                  {drawer(true, true)}
                </MobileDrawer>
              </>
            )}
          </>
        )}

        {/* Main Content */}
        <MainContent>
          <RouteTransitionStart />
          <Suspense
            fallback={
              <PageLoader
                onLoadingStart={startApiLoading}
                onLoadingEnd={endApiLoading}
              />
            }
          >
            <RouteTransitionReady />
            <Box
              sx={{
                padding: 0,
                minHeight: "100%",
                minWidth: 0,
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
                // Do not set overflowX here — it breaks position:sticky on GenericHeader
              }}
            >
              <HomSetupGuard appType={appType}>
                <Box sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}>
                  {authenticated && !canShowAppShell && appType !== "WEBSITE-" ? (
                    <AccessDenied onLogout={logout} />
                  ) : (
                    children
                  )}
                </Box>
              </HomSetupGuard>
            </Box>
          </Suspense>
        </MainContent>
      </LayoutContainer>
    </MobileDrawerProvider>
  );
}

export default ResponsiveDrawer;